/**
 * Plotwise Seeding Weather & Soil Temperature Data Service
 *
 * Henter realtids- og prognosedata for jordtemperatur i forskellige dybder
 * (0 cm, 6 cm, 18 cm), jordfugtighed, lufttemperatur og 7-dages nedbør
 * for præcise koordinater i Danmark.
 *
 * Benytter DWD ICON & ECMWF-modellerne via Open-Meteo (højopløselig landbrugsmodel)
 * med 60 minutters klient-caching for at minimere netværkstrafik og sikre lynhurtig indlæsning.
 */

export interface SoilWeatherData {
  latitude: number;
  longitude: number;
  elevation: number;
  timezone: string;
  fetchedAt: string;
  current: {
    time: string;
    temperature2m: number;
    soilTemperature0cm: number;
    soilTemperature6cm: number;
    soilTemperature18cm: number;
    soilMoisture0to1cm: number;
  };
  daily: Array<{
    date: string;
    tempMax: number;
    tempMin: number;
    precipitationSumMm: number;
    soilTemp6cmAvg: number;
  }>;
}

export type SeedingSuitability = 'optimal' | 'preparation' | 'cold' | 'wet' | 'off_season';

export interface SeedingWindowAssessment {
  suitability: SeedingSuitability;
  headline: string;
  advice: string;
  soilTempCurrentC: number;
  soilTempTargetC: number;
  expectedRainNext3DaysMm: number;
  soilMoisturePercent: number;
  optimalDateRange?: string;
  sowingPeriodNotice?: string;
}

/**
 * Sikrer korrekt fortolkning af koordinater for revirer i Danmark / Nordeuropa.
 * I Plotwise GeoJSON er estate.center typisk gemt som [lng, lat] (f.eks. [11.81, 55.40]).
 * Denne funktion beskytter mod utilsigtet koordinat-ombytning (hvor lat og lng byttes rundt),
 * hvilket ellers ville placere reviret i det ækvatoriale Afrika / Oman (hvor jordtemperaturen er ~29 °C i stedet for ~9 °C).
 */
export function resolveEstateLatLng(center?: [number, number] | null): {
  lat: number;
  lng: number;
} {
  if (!center || !Array.isArray(center) || center.length < 2) {
    return { lat: 55.4, lng: 11.8 };
  }
  const [c0, c1] = center;
  // I Danmark / Nordeuropa:
  // Breddegrad (Latitude) er altid ca. 54° til 58° N.
  // Længdegrad (Longitude) er altid ca. 8° til 15.5° E.
  if (c0 > 50 && c1 < 25) {
    return { lat: c0, lng: c1 };
  }
  if (c1 > 50 && c0 < 25) {
    return { lat: c1, lng: c0 };
  }
  return { lat: c1 || 55.4, lng: c0 || 11.8 };
}

const CACHE_TTL_MS = 60 * 60 * 1000; // 60 minutter
const memoryCache = new Map<string, { data: SoilWeatherData; expiresAt: number }>();

function cacheKey(lat: number, lng: number): string {
  // Afrund til 2 decimaler (~1,1 km præcision) til vejrdata
  return `plotwise_soil_weather_${lat.toFixed(2)}_${lng.toFixed(2)}`;
}

/**
 * Henter jordtemperatur og vejrudsigt for et revir/mark.
 */
export async function fetchSoilWeatherData(
  lat: number,
  lng: number,
  fetcher: typeof fetch = fetch,
): Promise<SoilWeatherData> {
  // Dobbelt-sikring: hvis en kalder alligevel sender ombyttede koordinater (lng før lat i Danmark)
  let safeLat = lat;
  let safeLng = lng;
  if (safeLat < 25 && safeLng > 50) {
    safeLat = lng;
    safeLng = lat;
  }

  const key = cacheKey(safeLat, safeLng);
  const now = Date.now();

  // 1. Tjek in-memory cache
  const inMem = memoryCache.get(key);
  if (inMem && inMem.expiresAt > now) {
    return inMem.data;
  }

  // 2. Tjek localStorage cache
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored) as { data: SoilWeatherData; expiresAt: number };
        if (parsed.expiresAt > now) {
          memoryCache.set(key, parsed);
          return parsed.data;
        }
      }
    } catch {
      // Ignorer localStorage fejl
    }
  }

  // 3. Hent friske data fra Open-Meteo højopløselig landbrugsmodel
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${safeLat.toFixed(4)}&longitude=${safeLng.toFixed(4)}&current=temperature_2m,soil_temperature_0cm,soil_temperature_6cm,soil_temperature_18cm,soil_moisture_0_to_1cm&hourly=soil_temperature_6cm&daily=temperature_2m_max,temperature_2m_min,precipitation_sum&timezone=Europe%2FCopenhagen&forecast_days=7`;

  try {
    const res = await fetcher(url, { signal: AbortSignal.timeout(6000) });
    if (!res.ok) {
      throw new Error(`Open-Meteo returnerede HTTP ${res.status}`);
    }

    const json = await res.json();
    const data = parseOpenMeteoResponse(json, safeLat, safeLng);

    // Gem i caches
    const cacheEntry = { data, expiresAt: now + CACHE_TTL_MS };
    memoryCache.set(key, cacheEntry);

    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.setItem(key, JSON.stringify(cacheEntry));
      } catch {
        // Ignorer localStorage quotafejl
      }
    }

    return data;
  } catch (err) {
    // Hvis offline eller API nede, returner et gennemsnitligt dansk forårsestimat
    return getFallbackSoilWeatherData(safeLat, safeLng);
  }
}

/**
 * Parser API-svaret til en ren, typesikker datamodel
 */
function parseOpenMeteoResponse(json: any, lat: number, lng: number): SoilWeatherData {
  const current = json.current || {};
  const daily = json.daily || {};
  const hourly = json.hourly || {};

  // Beregn daglig gennemsnitlig jordtemperatur i 6 cm dybde
  const dailyList: SoilWeatherData['daily'] = [];
  const times = daily.time || [];
  const hourlyTimes: string[] = hourly.time || [];
  const hourlySoil6: number[] = hourly.soil_temperature_6cm || [];

  for (let i = 0; i < times.length; i++) {
    const dayStr = times[i];
    // Find alle timer for denne dag
    const dayTemps: number[] = [];
    for (let h = 0; h < hourlyTimes.length; h++) {
      if (hourlyTimes[h]?.startsWith(dayStr) && typeof hourlySoil6[h] === 'number') {
        dayTemps.push(hourlySoil6[h]);
      }
    }
    const avgSoil =
      dayTemps.length > 0
        ? dayTemps.reduce((a, b) => a + b, 0) / dayTemps.length
        : (current.soil_temperature_6cm ?? 8.5);

    dailyList.push({
      date: dayStr,
      tempMax: daily.temperature_2m_max?.[i] ?? 12,
      tempMin: daily.temperature_2m_min?.[i] ?? 5,
      precipitationSumMm: daily.precipitation_sum?.[i] ?? 0,
      soilTemp6cmAvg: Number(avgSoil.toFixed(1)),
    });
  }

  return {
    latitude: json.latitude ?? lat,
    longitude: json.longitude ?? lng,
    elevation: json.elevation ?? 35,
    timezone: json.timezone ?? 'Europe/Copenhagen',
    fetchedAt: new Date().toISOString(),
    current: {
      time: current.time ?? new Date().toISOString(),
      temperature2m: Number(current.temperature_2m ?? 11),
      soilTemperature0cm: Number(current.soil_temperature_0cm ?? 9.5),
      soilTemperature6cm: Number(current.soil_temperature_6cm ?? 8.8),
      soilTemperature18cm: Number(current.soil_temperature_18cm ?? 9.0),
      soilMoisture0to1cm: Number(current.soil_moisture_0_to_1cm ?? 0.32),
    },
    daily: dailyList,
  };
}

/**
 * Robust fallback hvis netværket er offline
 */
export function getFallbackSoilWeatherData(lat: number, lng: number): SoilWeatherData {
  const today = new Date();
  const daily: SoilWeatherData['daily'] = [];

  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    const dateStr = d.toISOString().split('T')[0];
    daily.push({
      date: dateStr,
      tempMax: 12 + (i % 3),
      tempMin: 5 + (i % 2),
      precipitationSumMm: i === 2 ? 4.5 : 0,
      soilTemp6cmAvg: 8.5 + i * 0.3,
    });
  }

  return {
    latitude: lat,
    longitude: lng,
    elevation: 35,
    timezone: 'Europe/Copenhagen',
    fetchedAt: new Date().toISOString(),
    current: {
      time: new Date().toISOString(),
      temperature2m: 11.2,
      soilTemperature0cm: 9.8,
      soilTemperature6cm: 8.6,
      soilTemperature18cm: 8.9,
      soilMoisture0to1cm: 0.35,
    },
    daily,
  };
}

const DANISH_MONTHS: Record<string, number> = {
  januar: 1,
  jan: 1,
  februar: 2,
  feb: 2,
  marts: 3,
  mar: 3,
  april: 4,
  apr: 4,
  maj: 5,
  juni: 6,
  jun: 6,
  juli: 7,
  jul: 7,
  august: 8,
  aug: 8,
  september: 9,
  sep: 9,
  oktober: 10,
  okt: 10,
  november: 11,
  nov: 11,
  december: 12,
  dec: 12,
};

const DANISH_MONTH_NAMES = [
  'januar',
  'februar',
  'marts',
  'april',
  'maj',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'december',
];

/**
 * Udtrækker start- og slutmåned fra en dansk såperiodebeskrivelse (f.eks. "Maj – Juni")
 */
export function parseSowingMonths(periodText?: string): {
  startMonth: number;
  endMonth: number;
  seasonText: string;
} | null {
  if (!periodText || typeof periodText !== 'string') return null;
  const lower = periodText.toLowerCase();

  const matches: Array<{ month: number; index: number; name: string }> = [];
  const regex =
    /\b(januar|februar|marts|april|maj|juni|juli|august|september|oktober|november|december)\b/gi;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(lower)) !== null) {
    const raw = m[1].toLowerCase();
    const monthNum = DANISH_MONTHS[raw];
    if (monthNum) {
      matches.push({ month: monthNum, index: m.index, name: m[1] });
    }
  }

  if (matches.length === 0) return null;

  const startMonth = matches[0].month;
  const endMonth = matches.length > 1 ? matches[matches.length - 1].month : startMonth;
  const formatName = (mIdx: number) =>
    DANISH_MONTH_NAMES[mIdx - 1].charAt(0).toUpperCase() + DANISH_MONTH_NAMES[mIdx - 1].slice(1);

  const seasonText =
    matches.length > 1 && startMonth !== endMonth
      ? `${formatName(startMonth)} – ${formatName(endMonth)}`
      : formatName(startMonth);

  return { startMonth, endMonth, seasonText };
}

/**
 * Tjekker om en given dato (eller dags dato) ligger inden for den faglige såperiode
 */
export function isMonthInSowingPeriod(
  periodText?: string,
  date: Date = new Date(),
): {
  inSeason: boolean;
  currentMonth: number;
  seasonInfo: ReturnType<typeof parseSowingMonths>;
} {
  const seasonInfo = parseSowingMonths(periodText);
  const currentMonth = date.getMonth() + 1; // 1-12

  if (!seasonInfo) {
    return { inSeason: true, currentMonth, seasonInfo: null };
  }

  const { startMonth, endMonth } = seasonInfo;
  let inSeason = false;
  if (startMonth <= endMonth) {
    inSeason = currentMonth >= startMonth && currentMonth <= endMonth;
  } else {
    // Overlap e.g. September til Marts
    inSeason = currentMonth >= startMonth || currentMonth <= endMonth;
  }

  return { inSeason, currentMonth, seasonInfo };
}

/**
 * Evaluerer så-vinduet for en given frøblanding og minimums-jordtemperatur
 */
export function evaluateSeedingWindow(
  targetSoilTempC: number,
  weather: SoilWeatherData,
  options: {
    productName?: string;
    isSuperficialSeed?: boolean; // Overfladesåning (< 1 cm)
    sowingPeriod?: string;
    currentDate?: Date;
  } = {},
): SeedingWindowAssessment {
  const currentTemp = options.isSuperficialSeed
    ? weather.current.soilTemperature0cm
    : weather.current.soilTemperature6cm;

  const moistureFraction = weather.current.soilMoisture0to1cm;
  const moisturePct = Math.round(moistureFraction * 100);

  // Samlet nedbør de næste 3 dage
  const next3DaysRain = weather.daily
    .slice(0, 3)
    .reduce((sum, d) => sum + (d.precipitationSumMm || 0), 0);

  // FAGLIG SÅPERIODE KONTROL (Kritisk for at undgå fejlagtig såning i f.eks. oktober)
  const now = options.currentDate || new Date();
  const seasonCheck = isMonthInSowingPeriod(options.sowingPeriod, now);

  if (!seasonCheck.inSeason && seasonCheck.seasonInfo) {
    const currentMonthName = DANISH_MONTH_NAMES[seasonCheck.currentMonth - 1];
    const capitalizedMonth = currentMonthName.charAt(0).toUpperCase() + currentMonthName.slice(1);
    const prodName = options.productName || 'denne blanding';

    return {
      suitability: 'off_season',
      headline: 'Uden for såperiode',
      advice: `Den faglige såperiode for ${prodName} er ${seasonCheck.seasonInfo.seasonText}. Selvom jordtemperaturen aktuelt er ${currentTemp.toFixed(1)} °C, er vi i ${currentMonthName}. Udsåning nu frarådes kraftigt, da planterne ikke kan nå at etablere sig før nattefrosten sætter ind, hvilket medfører udvintring og totalt afgrødetab. Såning bør afventes til foråret/den anbefalede såperiode.`,
      soilTempCurrentC: currentTemp,
      soilTempTargetC: targetSoilTempC,
      expectedRainNext3DaysMm: Number(next3DaysRain.toFixed(1)),
      soilMoisturePercent: moisturePct,
      sowingPeriodNotice: `Anbefalet periode: ${seasonCheck.seasonInfo.seasonText} (aktuel måned: ${capitalizedMonth})`,
    };
  }

  // Jordforhold: for vådt (> 48 % fugt) eller ekstremt tørt (< 15 %)
  const isTooWet = moistureFraction > 0.48;

  // Temperaturstatus
  const tempDiff = currentTemp - targetSoilTempC;

  if (isTooWet) {
    return {
      suitability: 'wet',
      headline: 'Jorden er for våd til såning',
      advice: `Såbedet har et højt vandindhold (${moisturePct} %). Kørsel i våd jord pakker såsengen og skader jordstrukturen. Afvent 2-3 tørvejrsdage før harvning og såning.`,
      soilTempCurrentC: currentTemp,
      soilTempTargetC: targetSoilTempC,
      expectedRainNext3DaysMm: Number(next3DaysRain.toFixed(1)),
      soilMoisturePercent: moisturePct,
    };
  }

  if (tempDiff >= 0) {
    // Temperaturen er godkendt!
    let advice = `Jordtemperaturen er ${currentTemp.toFixed(1)} °C, hvilket overstiger minimumskravet på ${targetSoilTempC} °C.`;

    if (next3DaysRain >= 3 && next3DaysRain <= 15) {
      advice += ` Der er varslet ${next3DaysRain.toFixed(1)} mm regn de kommende dage, hvilket giver ideel kapillærfugtighed og hurtig fremspiring. Troml grundigt efter såning.`;
    } else if (next3DaysRain > 15) {
      advice += ` Kraftig nedbør (${next3DaysRain.toFixed(1)} mm) er varslet. Så og troml hurtigst muligt før regnen, eller vent til efter uvejret for at undgå at frøene skyller væk.`;
    } else {
      advice += ` Vejret er tørt. Tromling efter såning er afgørende for at bevare dybdefugtigheden omkring frøet.`;
    }

    return {
      suitability: 'optimal',
      headline: 'Optimalt så-vindue',
      advice,
      soilTempCurrentC: currentTemp,
      soilTempTargetC: targetSoilTempC,
      expectedRainNext3DaysMm: Number(next3DaysRain.toFixed(1)),
      soilMoisturePercent: moisturePct,
    };
  }

  // Hvis temperaturen er tæt på (inden for 1,5 grader) og stiger i de næste 3 dage
  const futureSoilAvg =
    weather.daily.slice(0, 3).reduce((sum, d) => sum + d.soilTemp6cmAvg, 0) /
    Math.min(weather.daily.length, 3);

  if (targetSoilTempC - currentTemp <= 1.8 && futureSoilAvg >= targetSoilTempC - 0.5) {
    return {
      suitability: 'preparation',
      headline: 'Gør klar: Så-vindue åbner snart',
      advice: `Jordtemperaturen er ${currentTemp.toFixed(1)} °C, lige under kravet på ${targetSoilTempC} °C, men prognosen viser stigende jordtemperatur over de næste 2–4 dage. Det anbefales at lave falsk såbed/let overfladeharvning nu for at bekæmpe ukrudtskim.`,
      soilTempCurrentC: currentTemp,
      soilTempTargetC: targetSoilTempC,
      expectedRainNext3DaysMm: Number(next3DaysRain.toFixed(1)),
      soilMoisturePercent: moisturePct,
    };
  }

  return {
    suitability: 'cold',
    headline: 'For kold jord til såning',
    advice: `Jordtemperaturen er kun ${currentTemp.toFixed(1)} °C (minimumskrav er ${targetSoilTempC} °C). For tidlig såning i kold jord giver langsom spiring, høj risiko for svampeangreb og fugle, der æder udsæden. Hold såmaskinen i laden indtil jordtemperaturen stiger stabilt.`,
    soilTempCurrentC: currentTemp,
    soilTempTargetC: targetSoilTempC,
    expectedRainNext3DaysMm: Number(next3DaysRain.toFixed(1)),
    soilMoisturePercent: moisturePct,
  };
}
